import React, { createContext, useContext, useCallback, useMemo, useState, useEffect } from 'react';
import { useFetchData } from "hooks/apiHooks";
import { useFileUpload } from "hooks/useFileUpload";
import { useEmailAnswerHistory } from 'hooks/useEmailAnswerHistory';
import { EDITABLE_KEYS } from 'constants';
import useSSE from 'hooks/useSSE';
import { SSE_CHANNELS } from 'constants';
import { SSE_TASKS } from 'constants';
import { OBJ } from 'constants';
import { useEmailAnalytics } from 'hooks/emailWritingHooks';
import { API_ENDPOINTS } from 'constants';
import { NEW_ITEM_ID } from 'constants';
import { useNewEmailDraft, replaceSignatureInBody } from 'hooks/emailHooks';
import { useComponentDetails } from 'hooks/useComponentDetails';

const EmailContext = createContext();

export function EmailProvider({ children, selectedItem, setSelectedItem, context, list }) {
    const [selectedText, setSelectedText] = useState('');
    const [view, setView] = useState('email');
    const [sidePaneMode, setSidePaneMode] = useState('individual');

    const { data: email, setData: setEmailData, saveKey, isLoading, error, saveError, refetch, toolbar} = useComponentDetails(API_ENDPOINTS.EMAILS.ITEM(selectedItem.id), list)
    const { triggerUpdate: sendEmail, updateSuccess: sendSuccess, updateError: sendError, isUpdating } = useFetchData(null, API_ENDPOINTS.EMAILS.SEND(selectedItem.id), false);

    const { data: emailToRespond, isLoading: isLoadingEmailToRespond, error: errorEmailToRespond, refetch: fetchEmailToRespond } = 
        useFetchData(API_ENDPOINTS.EMAILS.ITEM(email?.replied_to_email_id || -1), null, false);

    const { saveData: createAnswer, success: successCreateAnswer, saveError: saveErrorCreateAnswer } = 
        useFetchData(`/email/${selectedItem.id}/response`, null, false);

    const { sseSuccess, setSseSuccess, sseError } = useSSE(SSE_CHANNELS.EMAIL_UPDATE, SSE_TASKS.ANALYSE_EMAILS, true);
    
    const { standardSignature, fetchSignature, createDraft } = useNewEmailDraft(email?.account_settings_id);

    const emailToAnalyzeId = email?.replied_to_email_id || email?.id ;
    const { analysis, isLoading: isLoadingAnalysis, reAnalyze, triggerUpdateTable, updateAnalyticsKey, refetch: refetchAnalytics } = useEmailAnalytics(emailToAnalyzeId);

    useEffect(() => {
        if (sseSuccess) {
            if (sseSuccess?.id === email?.id) {
                refetch();
                refetchAnalytics();
                setSseSuccess(false);
                console.log('refresh for message', sseSuccess)
                list.refetch()
            }
            if (emailToRespond && sseSuccess?.id === emailToRespond?.id) {
                fetchEmailToRespond();
                setSseSuccess(false);
                console.log('refresh for message', sseSuccess)
                list.refetch()
            }
        }
    }, [sseSuccess, refetch, setSseSuccess]);

    const fileUpload = useFileUpload(API_ENDPOINTS.UPLOADS.FILE_UPLOAD(NEW_ITEM_ID), true);

    const isEditable = useMemo(() => email?.status === 'draft' || email?.status === 'suggested', [email?.status]);
    const isEmbedded = useMemo(() => context != null);
    const isNewEmail = email?.status === 'draft' && !email?.replied_to_email_id && !email?.replied_by_email_id
    const analyticsStatus = email?.status === "received" ? email?.analytics_status : emailToRespond?.analytics_status;


    const handleEmailChange = useCallback((key, value, isUserInput = true, debounce = true) => {
        if (EDITABLE_KEYS.has(key) || isEditable) {
            if(key === 'body' && value == null) {
                value = ""
            }
            console.log('save', email.id, key, value)
            saveKey(key, value, debounce);
        } else console.log('key not editible', key)
    }, [isEditable, saveKey]);

    const { versionControls, error: HistoryError, refetch: refetchHistory, saveError: saveErrorHistory, changeEditorAndVersion } = 
        useEmailAnswerHistory(email?.id, isEditable, handleEmailChange);
    
    const toggleOriginal = useCallback(async () => {
        if (email?.replied_to_email_id) {
            setSelectedItem({ type: OBJ.EMAILS, id: email?.replied_to_email_id })
        } 
    }, [email]);

    const toggleAnswer = useCallback(async () => {
        if (email?.replied_by_email_id) {
            setSelectedItem({ type: OBJ.EMAILS, id: email?.replied_by_email_id })
        } else {
            const draft = await createDraft(context?.type === OBJ.CUSTOMERS ? context.id : null, null, email?.account_settings_id);
            const newEmail = await createAnswer(draft);
            if (newEmail) {
                saveKey('replied_by_email_id', newEmail.id);
                setSelectedItem({ type: OBJ.EMAILS, id: newEmail.id })
            }
        }
    }, [email, createAnswer, saveKey]);

    const handleAccountSettingsChange = useCallback(async (newAccountSettingsId) => {
        const oldSignatureContent = standardSignature?.content || '';
        handleEmailChange('account_settings_id', newAccountSettingsId, true);

        if (!isEditable) return;

        const newSignature = await fetchSignature(newAccountSettingsId);
        const newBody = replaceSignatureInBody(email?.body, oldSignatureContent, newSignature?.content);
        if (newBody !== email?.body) {
            changeEditorAndVersion(newBody, false, true);
        }
    }, [standardSignature, handleEmailChange, isEditable, fetchSignature, email?.body, changeEditorAndVersion]);

    const handleChangeEditorAndVersion = useCallback((content, subject = null, recipients = null) => {
        //console.log('handleChangeEditorAndVersion', content, subject)
        changeEditorAndVersion(content, false, true);
        if (subject && (!email.subject || email.subject === '')) {
            console.log('handle change subject')
            handleEmailChange('subject', subject, false, true);
        }
        if (recipients && (!email.recipients || email.recipients === '')) {
            console.log('handle change recipient')
            handleEmailChange('recipients', recipients, false, true);
        }
    }, [email?.subject, handleEmailChange]);

    const emailState = useMemo(() => ({
        standardSignature, email, saveKey, emailToRespond, isLoading, error, saveError, sendSuccess, sendError, isUpdating
    }), [standardSignature, email, saveKey, emailToRespond, isLoading, error, saveError, sendSuccess, sendError, isUpdating]);
    
    const handlers = useMemo(() => ({
        handleEmailChange, handleAccountSettingsChange, toggleAnswer, sendEmail, 
        refetch, fetchEmailToRespond, changeEditorAndVersion, handleChangeEditorAndVersion
    }), [handleEmailChange, handleAccountSettingsChange, toggleAnswer, sendEmail, 
        refetch, fetchEmailToRespond, changeEditorAndVersion]);

        const toggleIsRead = () => {list.toggleLineKey(email.id, "is_read")}
        const toggleDelete = () => {list.toggleDelete(email.id)}
    
    const value = useMemo(() => ({
        ...emailState,
        ...handlers,
        fileUpload,
        versionControls,
        selectedItem,
        setSelectedItem,
        isEditable,
        isEmbedded,
        selectedText, 
        setSelectedText,
        successCreateAnswer,
        saveErrorCreateAnswer,
        isLoadingEmailToRespond,
        errorEmailToRespond,
        emailToAnalyzeId, analysis, reAnalyze, triggerUpdateTable, updateAnalyticsKey,
        analyticsStatus,
        isNewEmail,
        view, 
        setView,
        toggleIsRead,
        sseError,
        toggleOriginal,
        listLoading: list.isLoading,
        list,
        setEmailData,
        toggleDelete, sidePaneMode, setSidePaneMode, isLoadingAnalysis,
        toolbar
    }), [emailState, handlers, versionControls, selectedItem,
        setSelectedItem, isEditable, isEmbedded, selectedText, 
        setSelectedText, successCreateAnswer, saveErrorCreateAnswer, isLoadingEmailToRespond,
        errorEmailToRespond, emailToAnalyzeId, analysis, reAnalyze, triggerUpdateTable, updateAnalyticsKey,
        sidePaneMode, setSidePaneMode, isLoadingAnalysis,
        toolbar]);

    return <EmailContext.Provider value={value}>{children}</EmailContext.Provider>;
}

export function useEmailContext() {
    const context = useContext(EmailContext);
    if (!context) {
        throw new Error('useEmailContext must be used within an EmailProvider');
    }
    return context;
}